"use client";

import {
  Toast,
  type ToastActionElement,
  ToastClose,
  ToastDescription,
  ToastProvider,
  type ToastProps,
  ToastTitle,
  ToastViewport,
} from "@/components/ui/toast";
import { useToast } from "@/hooks/use-toast";
import { CheckCircle2, XCircle, AlertTriangle, Info } from "lucide-react";
import { type ReactNode, useEffect, useState } from "react";

const getToastIcon = (variant?: string | null) => {
  switch (variant) {
    case "success":
      return (
        <CheckCircle2 className="h-5 w-5 text-green-600 dark:text-green-400" />
      );
    case "destructive":
      return <XCircle className="h-5 w-5 text-red-600 dark:text-red-400" />;
    case "warning":
      return (
        <AlertTriangle className="h-5 w-5 text-yellow-600 dark:text-yellow-400" />
      );
    default:
      return <Info className="text-primary dark:text-primary h-5 w-5" />;
  }
};

export function ToastMessage({
  title,
  description,
  action,
  variant,
}: {
  title?: ReactNode;
  description?: ReactNode;
  action?: ToastActionElement;
  variant?: ToastProps["variant"];
}) {
  const icon = getToastIcon(variant);

  if (!title) {
    return (
      <div className="min-w-0 flex-1">
        <div className="flex items-start gap-2 pr-4">
          <div className="mt-0.5 shrink-0" aria-hidden="true">
            {icon}
          </div>
          {description && (
            <ToastDescription className="leading-5">
              {description}
            </ToastDescription>
          )}
        </div>
        {action && <div className="mt-3 flex justify-start">{action}</div>}
      </div>
    );
  }

  return (
    <div className="grid min-w-0 flex-1 gap-2">
      <div className="flex items-center gap-2 pr-4">
        <div className="shrink-0" aria-hidden="true">
          {icon}
        </div>
        <ToastTitle>{title}</ToastTitle>
      </div>
      {description && (
        <ToastDescription className="leading-5">{description}</ToastDescription>
      )}
      {action && <div className="mt-1 flex justify-start">{action}</div>}
    </div>
  );
}

export function Toaster() {
  const { toasts } = useToast();
  const [isMounted, setIsMounted] = useState(false);

  useEffect(() => {
    setIsMounted(true);
  }, []);

  if (!isMounted) {
    return null;
  }

  return (
    <ToastProvider>
      {toasts.map(function ({
        id,
        title,
        description,
        action,
        variant,
        ...props
      }) {
        return (
          <Toast key={id} variant={variant} {...props}>
            <ToastMessage
              title={title}
              description={description}
              action={action}
              variant={variant}
            />
            <ToastClose />
          </Toast>
        );
      })}
      <ToastViewport />
    </ToastProvider>
  );
}
